<template>
  <UiComponentContainer class="page">
    <OrganismHero>
      <template #tag>
        {{ data.page.tag }}
      </template>
      <template #heading>
        {{ data.page.title }}
      </template>
      <template #paragraph>
        {{ data.page.subtitle }}
      </template>
    </OrganismHero>
    <UiComponentWrapper class="filters">
      <MoleculeFormField
        id="industries"
        name="industries"
        of-type="select"
        :options="data.allBenchmarksIndustries.map((industry) => industry.name)"
        @field:change="selectIndustry"
      >
        {{ data.page.industrySelect }}
      </MoleculeFormField>
      <MoleculeFormField
        id="registrants"
        :min="0"
        name="registrants"
        of-type="input"
        placeholder="0"
        type="number"
        @field:change="setNumberOfRegistrants"
      >
        {{ data.page.industrySelectRegistrantsPlaceholder }}
      </MoleculeFormField>
      <MoleculeFormField
        id="participants"
        :max="registrants"
        :min="0"
        name="participants"
        of-type="input"
        placeholder="0"
        type="number"
        @field:change="setNumberOfParticipants"
      >
        {{ data.page.industrySelectParticipantsPlaceholder }}
      </MoleculeFormField>
      <AtomButton
        :disabled="!formIsSubmittable"
        type="secondary"
        @click="onSubmit"
      >
        {{
          data.page.industrySelectSubmit
        }}
      </AtomButton>
    </UiComponentWrapper>
    <UiComponentWrapper
      v-for="(chart, index) in data.allCharts"
      :key="chart.id"
      class="inner"
      :class="{ odd: index % 2 === 1 }"
    >
      <UiComponentWrapper class="content">
        <UiComponentWrapper class="paragraph">
          <UiComponentWrapper
            v-for="({ text }, subindex) in chart.extendedDescription"
            :key="`${chart.id}-md-${subindex}`"
          >
            <OrganismRichText :content="[{ __typename: 'ContentTextHtmlRecord', text }]" />
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentWrapper
          v-if="index === 0 && (textRatio || textRatioDiff)"
          class="ratios"
        >
          <UiComponentParagraph v-if="textRatio">
            {{ textRatio }}
          </UiComponentParagraph>
          <UiComponentParagraph v-if="textRatioDiff">
            {{ textRatioDiff }}
          </UiComponentParagraph>
        </UiComponentWrapper>
        <AtomButton
          size="l"
          type="secondary"
          @click="handleTwitterClick(chart)"
        >
          <img
            alt="twitter"
            height="13"
            loading="lazy"
            src="@/assets/svg/twitter.svg"
            width="16"
          >
          Tweet
        </AtomButton>
      </UiComponentWrapper>
      <UiComponentWrapper class="chart">
        <Bar
          ref="$charts"
          :data="chartData.find((x) => x.id === chart.chartId).data"
          :chart-id="chart.chartId"
          :options="chartData.find((x) => x.id === chart.chartId).options"
        />
      </UiComponentWrapper>
    </UiComponentWrapper>
    <OrganismCallToAction
      :features="features"
      :primary-button="signupOrUpgradeCallToAction"
      :secondary-button="requestDemoCallToAction"
      @secondary:click="setModalFormVisibility(true)"
    >
      <template #heading>
        {{ data.componentGetStarted.title }}
      </template>
      <template #paragraph>
        {{ data.componentGetStarted.paragraph }}
      </template>
    </OrganismCallToAction>
  </UiComponentContainer>
</template>

<script setup>
import { Chart, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { Bar } from 'vue-chartjs'
import attendanceData from '@/assets/data/import-avg-attendance-per-industry.json'
import durationData from '@/assets/data/import-avg-duration-per-industry.json'
import registrantData from '@/assets/data/import-avg-registrants-per-weekday.json'
import sessionData from '@/assets/data/import-avg-webinars-per-weekday.json'

Chart.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

// Layout
definePageMeta({
  layout: 'default',
})

// i18n
defineI18nRoute({
  locales: ['en', 'es', 'fr'],
  paths: {
    en: '/webinar-statistics',
    es: '/estadisticas-seminarios-web',
    fr: '/webinar-statistiques',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Async Data
const { data } = await useFetch(`/api/content/pages/webinar-statistics/${locale.value}`, {
  key: `page-webinar-statistics-${locale.value}`,
  query: {
    preview: state.preview,
  },
})

// Refs
const $charts = ref(null)
const baseChartData = ref({
  avg: registrantData.map(item => Math.floor(item.ratioRegistrants)),
  weekDay: registrantData.map(item => item.weekDay[locale.value]),
  avgSessions: sessionData.map(item => item.ratioSessionsEnded),
  industry: attendanceData.map(item => item.industry[locale.value]),
  attendanceRatio: attendanceData.map(item => Math.floor(item.attendanceRatio)),
  industryOfDuration: durationData.map(item => item.industry[locale.value]),
  avgDuration: durationData.map(item => Math.floor(item.avgDuration)),
})
const formIsSubmittable = ref(true)
const participants = ref(0)
const ratio = ref(null)
const registrants = ref(0)
const selectedItem = ref('')

// Meta
const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useHead({
  link: head.value.link,
  meta: head.value.meta,
})

useSeoMeta({
  title: data.value.page.titleSeo,
  description: data.value.page.descriptionSeo,
  ogTitle: data.value.page.titleSeo,
  ogDescription: data.value.page.descriptionSeo,
  twitterTitle: data.value.page.titleSeo,
  twitterDescription: data.value.page.descriptionSeo,
})

// Computed Properties
const chartData = computed(() => {
  return [
    {
      id: 'attendanceChart',
      data: {
        labels: baseChartData.value.industry,
        datasets: [
          {
            label: 'Avg attendance ratio',
            data: baseChartData.value.attendanceRatio,
            hoverBackgroundColor: '#111958',
            backgroundColor: [
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
            ],
            borderWidth: 0,
          },
        ],
      },
      options: {
        annotation: {
          annotations: [
            {
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 30,
              borderColor: '#BA2A35',
              borderDash: [5, 10],
              borderWidth: 2,
              label: {
                content: data.value.page.softwareAvg,
                backgroundColor: '#111958',
                fontColor: 'white',
                enabled: true,
                position: 'right',
              },
            },
            {
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 46,
              borderColor: '#32855B',
              borderDash: [5, 10],
              borderWidth: 2,
              label: {
                content: data.value.page.livestormAvg,
                backgroundColor: '#111958',
                fontColor: 'white',
                enabled: true,
                position: 'right',
              },
            },
          ],
        },
        legend: {
          display: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderWidth: 10,
        },
      },
    },
    {
      id: 'durationChart',
      data: {
        labels: baseChartData.value.industryOfDuration,
        datasets: [
          {
            label: 'Avg duration',
            data: baseChartData.value.avgDuration,
            hoverBackgroundColor: '#111958',
            backgroundColor: [
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
              '#3059DD',
            ],
            borderWidth: 0,
          },
        ],
      },
      options: {
        annotation: {
          annotations: [
            {
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 41,
              borderColor: '#32855B',
              borderDash: [5, 10],
              borderWidth: 2,
              label: {
                content: data.value.page.durationAvg,
                backgroundColor: '#111958',
                fontColor: 'white',
                enabled: true,
                position: 'right',
              },
            },
          ],
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        tooltips: {
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderWidth: 10,
        },
      },
    },
    {
      id: 'participantsChart',
      data: {
        labels: baseChartData.value.weekDay,
        datasets: [
          {
            label: data.value.page.participantLabel,
            data: baseChartData.value.avg,
            hoverBackgroundColor: '#111958',
            backgroundColor: [
              '#DCE8F9',
              '#3059DD',
              '#3059DD',
              '#DCE8F9',
              '#DCE8F9',
              '#DCE8F9',
              '#DCE8F9',
            ],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        tooltips: {
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderWidth: 10,
        },
      },
    },
    {
      id: 'avgSessionsChart',
      data: {
        labels: baseChartData.value.weekDay,
        datasets: [
          {
            label: 'Average number',
            data: baseChartData.value.avgSessions,
            hoverBackgroundColor: '#111958',
            backgroundColor: [
              '#DCE8F9',
              '#DCE8F9',
              '#3059DD',
              '#3059DD',
              '#DCE8F9',
              '#DCE8F9',
              '#DCE8F9',
            ],
            borderWidth: 0,
          },
        ],
      },
      options: {
        legend: {
          display: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderWidth: 10,
        },
      },
    },
  ]
})

const features = computed(() => {
  return [data.value.componentGlobal.noCreditCard, data.value.componentGlobal.noInstall]
})

const ratioDiff = computed(() => {
  if (!Number.isNaN(ratio.value)) {
    return 46 - ratio.value
  }

  return undefined
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: data.value.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaTryForFreeShort,
      }
})

const textRatio = computed(() => {
  if (!ratio.value) {
    return undefined
  }

  return `${data.value.page.participateRate} ${ratio.value}%.`
})

const textRatioDiff = computed(() => {
  if (ratioDiff.value > 100) {
    return data.value.page.ratio100
  }

  if (ratioDiff.value <= 0) {
    return data.value.page.impressive
  }

  if (ratioDiff.value.valueDiff > 0) {
    return `${data.value.page.withLivestorm} ${ratioDiff.value} ${data.value.page.points}.`
  }

  return undefined
})

// Methods
function handleTwitterClick(chart) {
  if (import.meta.client) {
    const url = window.location.href.split('#')[0]
    const text = `${data.value.page.title} - ${chart.title}: ${selectedItem.value}`
    const fullURL = encodeURIComponent(`${url}#${chart.chartId}`)
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${fullURL}&via=livestormapp`,
    )
  }
}

function onSubmit() {
  if (participants.value > registrants.value) {
    return
  }

  setRatio()
  updateConfigByMutating()
}

function selectIndustry(option) {
  selectedItem.value = option
}

function setNumberOfParticipants(count) {
  participants.value = Number.parseInt(count)
  validateForm()
}

function setNumberOfRegistrants(count) {
  registrants.value = Number.parseInt(count)
  validateForm()
}

function setRatio() {
  const newRatio = (participants.value / registrants.value) * 100
  ratio.value = parseInt(newRatio.toFixed(0))
}

function updateConfigByMutating() {
  ($charts.value || []).forEach((child) => {
    const ids = ['attendanceChart', 'durationChart']
    if (ids.includes(child.canvasId) && selectedItem.value) {
      const { labels } = child.chartInstance.data
      for (let i = 0; i < child.chartInstance.data.datasets[0].backgroundColor.length; i++) {
        child.chartInstance.data.datasets[0].backgroundColor[i] = '#DCE8F9'
        if (labels[i] === selectedItem.value) {
          child.chartInstance.data.datasets[0].backgroundColor[i] = '#3059DD'
        }
      }
      child.update()
    }
  })
}

function validateForm() {
  formIsSubmittable.value
    = registrants.value >= 0
    && participants.value >= 0
    && registrants.value >= participants.value
}

// Lifecycle hooks
onMounted(() => {
  page(data.value.page.trackName || data.value.page.trackNameFallback)
  selectIndustry(data.value.allBenchmarksIndustries.map(industry => industry.name)[0])
  setAvailableLanguages(['en', 'es', 'de'])
  setRatio()
  updateConfigByMutating()
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || data.value.page.title,
    },
  ])
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col;

  .hero {
    @apply py-8;

    @screen md {
      @apply py-16;
    }

    @screen lg {
      @apply py-20;
    }

    @screen xxxl {
      @apply py-26;
    }
  }

  .filters {
    @apply flex-col p-8 rounded-4xl border border-grey-blue-200;

    @screen md {
      @apply flex-row items-end py-12;
    }

    > * {
      @apply my-3;

      &:first-child {
        @apply mt-0;
      }

      &:last-child {
        @apply mb-0;
      }

      @screen md {
        @apply my-0 mx-3;

        &:first-child {
          @apply ml-0;
        }

        &:last-child {
          @apply mr-0;
        }
      }
    }
  }

  .inner {
    @apply flex-col mt-20;

    @screen mdminus {
      @apply mt-16;
    }

    @screen mdplus {
      @apply mt-26;
    }

    @screen xl {
      @apply flex-row justify-between;
    }

    &:nth-child(even) {
      .content {
        @screen xl {
          @apply order-last;
        }
      }
    }

    .content {
      @apply flex-col;

      @screen xl {
        @apply w-span6;
      }

      .paragraph {
        @apply flex-col;
      }

      .ratios {
        @apply mt-4 flex-col;

        p {
          @apply font-medium;
        }
      }

      .button {
        @apply self-start mt-4 bg-twitter-200 text-twitter-600;

        img {
          @apply mr-3;
        }
      }
    }

    .chart {
      @apply flex w-full mt-12 aspect-video;

      @screen xl {
        @apply self-start w-span5;
      }
    }
  }

  .call-to-action {
    @apply mt-20;

    @screen mdminus {
      @apply mt-16;
    }

    @screen mdplus {
      @apply mt-26;
    }
  }
}

.max-custom {
  max-width: 550px;
}
</style>
